import type {
  VideoCallConnectionState,
  VideoCallLimits,
  VideoCallParticipantState,
  VideoCallQualityProfile,
} from "~/types/videoCalls";

export const canStartVideoCall = ({
  isTeacher,
  featureEnabled,
}: {
  isTeacher: boolean;
  featureEnabled: boolean;
}) => isTeacher && featureEnabled;

export const getVideoCallCapacityState = (
  participantCount: number,
  limits: VideoCallLimits,
) => ({
  shouldWarn: participantCount >= limits.warning_participants,
  canAdmit: participantCount < limits.max_participants,
});

export const getAvailableMediaState = (stream: MediaStream | null) => ({
  cameraAvailable:
    stream?.getVideoTracks().some((track) => track.readyState !== "ended") ??
    false,
  microphoneAvailable:
    stream?.getAudioTracks().some((track) => track.readyState !== "ended") ??
    false,
});

export const getVideoCallMediaVisibleParticipantIds = ({
  compact,
  fullscreen,
  visibleParticipantIds,
  remotePresenterParticipantId,
}: {
  compact: boolean;
  fullscreen: boolean;
  visibleParticipantIds: string[];
  remotePresenterParticipantId: string | null;
}): string[] | null => {
  if (fullscreen) {
    return remotePresenterParticipantId ? [remotePresenterParticipantId] : [];
  }
  if (!compact) return null;

  return Array.from(
    new Set([
      ...visibleParticipantIds,
      ...(remotePresenterParticipantId ? [remotePresenterParticipantId] : []),
    ]),
  );
};

export const getVideoCallTilePresentation = ({
  local,
  cameraEnabled,
  qualityProfile,
}: {
  local: boolean;
  cameraEnabled: boolean;
  qualityProfile?: VideoCallQualityProfile | null;
}): "camera-off" | "adaptive-paused" | "adaptive-reduced" | "normal" => {
  if (!cameraEnabled) return "camera-off";
  if (local) return "normal";
  if (qualityProfile === "paused") return "adaptive-paused";
  if (qualityProfile === "low" || qualityProfile === "survival") {
    return "adaptive-reduced";
  }
  return "normal";
};

export const getPeerConnectionLabel = ({
  connectionState,
  participantState,
}: {
  connectionState?: RTCPeerConnectionState | null;
  participantState?: VideoCallParticipantState | null;
}) => {
  if (participantState === "admitted") return "Aguardando conexão";
  if (participantState === "disconnected") {
    return connectionState === "connected" ? "Reconectando..." : "Desconectado";
  }

  if (connectionState === "new" || connectionState === "connecting") {
    return "Conectando...";
  }
  if (connectionState === "disconnected") return "Reconectando...";
  if (connectionState === "failed") return "Falha na conexão";
  if (connectionState === "closed") return "Conexão encerrada";
  return "";
};

export const getVideoCallAttention = ({
  connectionState,
  errorMessage,
  pendingRequestCount,
}: {
  connectionState: VideoCallConnectionState;
  errorMessage: string;
  pendingRequestCount: number;
}): { kind: "error" | "reconnecting" | "pending"; message: string } | null => {
  if (errorMessage) {
    return { kind: "error", message: `Erro: ${errorMessage}` };
  }
  if (connectionState === "reconnecting") {
    return { kind: "reconnecting", message: "Reconectando a chamada..." };
  }
  if (pendingRequestCount > 0) {
    return {
      kind: "pending",
      message: `${pendingRequestCount} ${
        pendingRequestCount === 1 ? "pedido aguarda" : "pedidos aguardam"
      } aprovação`,
    };
  }
  return null;
};
